import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from 'context/AuthProvider';
import { notification } from 'antd';
import './NotificationProvider.scss';
import { notificationLink } from 'utils/notificationLink';
import { getNewNotifications, getUnreadNotificationCount, getNotificationPushConfig, registerNotificationDevice, unregisterNotificationDevice } from 'services/notification.service';
import { startRealtimeStream } from 'utils/realtimeStream';
import { AUTH_REFRESHED_EVENT } from 'utils/authSession';
import { PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import { canUseBrowserPush, getNotificationDeviceId, NOTIFICATION_PUSH_STORAGE_KEY, NOTIFICATION_LOGOUT_EVENT, NOTIFICATION_LOGOUT_CANCELLED_EVENT, prepareBrowserPush } from 'utils/notificationPush';

const NotificationContext = createContext({
  unreadCount: 0, revision: 0, status: 'connecting', pushStatus: 'loading', pushError: '',
  refresh: async () => {}, enablePush: async () => {}, disablePush: async () => {},
});
export const useNotifications = () => useContext(NotificationContext);

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const identity = user?.user_name || user?.user_id;
  const [unreadCount, setUnreadCount] = useState(0);
  const [revision, setRevision] = useState(0);
  const [status, setStatus] = useState('connecting');
  const [pushStatus, setPushStatus] = useState('loading');
  const [pushError, setPushError] = useState('');
  const config = useRef(null);
  const pushUnsubscribe = useRef(null);
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const refreshGeneration = useRef(0);
  const pushBusy = useRef(false);
  const pushGeneration = useRef(0);
  const logoutStarted = useRef(false);

  const refresh = useCallback(async () => {
    if (!identity) return;
    const generation = ++refreshGeneration.current;
    const data = await getUnreadNotificationCount();
    if (generation !== refreshGeneration.current || currentIdentity.current !== identity) return;
    setUnreadCount(Math.max(0, Number(data?.unread_count) || 0));
    setRevision(value => value + 1);
  }, [identity]);

  const enablePush = useCallback(async (askPermission = true) => {
    if (pushBusy.current || logoutStarted.current || !identity || !config.current?.enabled) return;
    pushBusy.current = true;
    const generation = ++pushGeneration.current;
    setPushError('');
    try {
      const permission = askPermission ? await Notification.requestPermission() : Notification.permission;
      if (permission !== 'granted') { setPushStatus(permission); return; }
      setPushStatus('registering');
      pushUnsubscribe.current?.();
      pushUnsubscribe.current = null;
      const result = await prepareBrowserPush(config.current, () => {
        if (currentIdentity.current === identity && generation === pushGeneration.current) refresh().catch(() => {});
      });
      if (currentIdentity.current !== identity || generation !== pushGeneration.current) { result.unsubscribe(); return; }
      pushUnsubscribe.current = result.unsubscribe;
      await registerNotificationDevice({ device_id: getNotificationDeviceId(), token: result.token, platform: 'web' });
      if (currentIdentity.current !== identity || generation !== pushGeneration.current) return;
      window.localStorage.setItem(NOTIFICATION_PUSH_STORAGE_KEY, 'true');
      setPushStatus('enabled');
    } catch (_) {
      if (currentIdentity.current === identity && generation === pushGeneration.current) {
        setPushStatus('error');
        setPushError('Chưa bật được thông báo trên trình duyệt. Kiểm tra quyền thông báo và thử lại.');
      }
    } finally { pushBusy.current = false; }
  }, [identity, refresh]);

  const disablePush = useCallback(async () => {
    if (pushBusy.current) return;
    pushBusy.current = true;
    setPushError('');
    try {
      await unregisterNotificationDevice(getNotificationDeviceId());
      window.localStorage.setItem(NOTIFICATION_PUSH_STORAGE_KEY, 'false');
      pushUnsubscribe.current?.();
      pushUnsubscribe.current = null;
      setPushStatus('default');
    } catch (_) { setPushError('Chưa tắt được thông báo. Vui lòng thử lại.'); }
    finally { pushBusy.current = false; }
  }, []);

  useEffect(() => {
    logoutStarted.current = false;
    refreshGeneration.current += 1;
    setUnreadCount(0); setRevision(0); setPushStatus('loading'); setPushError(''); config.current = null;
    if (!identity) { setStatus('disconnected'); return undefined; }
    let active = true;
    let stop;
    let lastServerRevision;
    let latestId;
    let arrivalGeneration = 0;
    const alertKeys = new Set();
    const announceArrivals = data => {
      const nextId = Number(data.latest_notify_user_id);
      if (!Number.isSafeInteger(nextId) || nextId < 0) return;
      const previousId = latestId;
      latestId = nextId;
      if (previousId === undefined || nextId <= previousId) return;
      const generation = arrivalGeneration;
      getNewNotifications(previousId).then(result => {
        if (!active || generation !== arrivalGeneration || currentIdentity.current !== identity || logoutStarted.current) return;
        const items = (result?.items || []).filter(item => !item.is_read && item.notify_user_id > previousId && item.notify_user_id <= nextId);
        items.slice(0,5).forEach(item => {
          const key = `work-notification-${identity}-${item.notify_user_id}`;
          if (alertKeys.has(key)) return;
          alertKeys.add(key);
          const link = notificationLink(item);
          const base = (process.env.REACT_APP_BASENAME || '').replace(/\/$/,'');
          notification.open({ key,message:item.notify_title,duration:12,placement:'topRight',
            description:<div><p>{item.notify_content}</p>{link && <a href={`${base}${link}`}>Xem nội dung</a>}</div> });
        });
      }).catch(() => {});
    };
    const start = () => {
      stop?.();
      stop = startRealtimeStream('/notify/realtime', {
        onStatus: value => active && setStatus(value),
        onUpdate: data => {
          if (!active) return;
          refreshGeneration.current += 1;
          announceArrivals(data);
          if (data.unread_count !== undefined) setUnreadCount(Math.max(0, Number(data.unread_count) || 0));
          if (data.notification_revision !== lastServerRevision) {
            lastServerRevision = data.notification_revision;
            setRevision(value => value + 1);
          }
        },
      });
    };
    const pausePush = () => {
      logoutStarted.current = true;
      pushGeneration.current += 1;
      pushUnsubscribe.current?.(); pushUnsubscribe.current = null;
      if (config.current?.enabled && canUseBrowserPush()) setPushStatus(Notification.permission === 'denied' ? 'denied' : 'default');
    };
    const cancelLogout = () => { logoutStarted.current = false; };
    const scopeChanged = () => { latestId=undefined; arrivalGeneration+=1; alertKeys.forEach(key => notification.close(key)); recover(); };
    const recover = () => {
      if (document.visibilityState === 'hidden') return;
      refresh().catch(() => {});
      start();
    };
    start();
    refresh().catch(() => {});
    getNotificationPushConfig().then(data => {
      if (!active) return;
      config.current = data;
      if (!data?.enabled) { setPushStatus('not-configured'); return; }
      if (!canUseBrowserPush()) { setPushStatus('unsupported'); return; }
      setPushStatus(Notification.permission);
      if (!logoutStarted.current && Notification.permission === 'granted' && window.localStorage.getItem(NOTIFICATION_PUSH_STORAGE_KEY) !== 'false') enablePush(false);
    }).catch(() => active && setPushStatus('not-configured'));
    window.addEventListener(NOTIFICATION_LOGOUT_EVENT, pausePush);
    window.addEventListener(NOTIFICATION_LOGOUT_CANCELLED_EVENT, cancelLogout);
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, scopeChanged);
    window.addEventListener(AUTH_REFRESHED_EVENT, start);
    window.addEventListener('online', recover);
    document.addEventListener('visibilitychange', recover);
    return () => {
      active = false; pushGeneration.current += 1; stop?.(); pushUnsubscribe.current?.(); pushUnsubscribe.current = null;
      window.removeEventListener(NOTIFICATION_LOGOUT_EVENT, pausePush);
      window.removeEventListener(NOTIFICATION_LOGOUT_CANCELLED_EVENT, cancelLogout);
      window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, scopeChanged);
      alertKeys.forEach(key => notification.close(key));
      window.removeEventListener(AUTH_REFRESHED_EVENT, start);
      window.removeEventListener('online', recover);
      document.removeEventListener('visibilitychange', recover);
    };
  }, [identity, refresh, enablePush]);

  return <NotificationContext.Provider value={{ unreadCount, revision, status, refresh, pushStatus, pushError, enablePush, disablePush }}>{children}</NotificationContext.Provider>;
}
